import 'dayjs/locale/ja';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HttpStatusCode } from 'axios';
import { Alert, Button, Modal, Table, TableColumnsType, message } from 'antd';
import { useAccountListStore } from './account_list_store';
import { AccountListReponse, AccountReponse } from '../../../model/account_response';
import { useIndicator } from '../../../hook/use_indicator';
import { ApiError } from '../../../api/api';
import { Messages, formatMessage } from '../../../constant/messages';
import { BusinessTypes, Roles } from '../../../constant';

type LocationState = {
  refresh?: boolean;
};
/**
 * アカウント一覧画面
 * @constructor
 */
export const AccountListPage: React.FC = () => {
  const navigator = useNavigate();
  const { getAccountList, deleteAccount } = useAccountListStore((selector) => selector);
  const [data, setData] = useState<AccountListReponse>();
  const indicator = useIndicator();
  const [errorCode, setErrorCode] = React.useState<number>(1);
  const [modal, showModal] = useState(false);
  const [targetUser, setTargetUser] = useState<AccountReponse | null>(null);
  const [messageApi, contextHolder] = message.useMessage();
  const location = useLocation() as ReturnType<typeof useLocation> & { state: LocationState };

  const fetchAccountList = () => {
    indicator.show();
    setErrorCode(0);
    getAccountList().then((res) => {
      setData(res);
    }).catch((e) => {
      if (e instanceof ApiError) {
        const { status } = e.response;
        if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
          window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
          navigator('/login', { state: { message: Messages.ERR_401 } });
        } else if (status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
        } else {
          window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
          // setErrorCode(status); 一覧上にエラーを出す場合、エラーコードをセットする
        }
      }
    }).finally(() => {
      indicator.hide();
    });
  };

  useEffect(() => {
    if (location.state?.refresh) {
      fetchAccountList();
      // stateクリア
      navigator(location.pathname, { replace: true });
      // 完了メッセージ
      messageApi.success(Messages.EXECUTE_ISSUE_REGISTER);
    } else {
      fetchAccountList();
    }
  }, []);

  const handleDelete = () => {
    if (targetUser === null) {
      return;
    }
    indicator.show();
    deleteAccount(targetUser.userId).then(() => {
      // 完了メッセージ
      messageApi.success(formatMessage(Messages.EXECUTE_DELETE_WITH_NAME, targetUser.accountId));
      // 画面リロード
      fetchAccountList();
    }).catch((e) => {
      if (e instanceof ApiError) {
        const { status } = e.response;
        if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
          window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
          navigator('/login', { state: { message: Messages.ERR_401 } });
        } else if (status === HttpStatusCode.NotFound) {
          messageApi.error(formatMessage(Messages.ERR_DIALOG_ALREADY_DELETED, targetUser.accountId));
        } else if (status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
        } else {
          window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
        }
      }
    }).finally(() => {
      indicator.hide();
      showModal(false);
      setTargetUser(null);
    });
  };

  const doCompareStr = (a: string, b: string) => {
    if (a === null || a === undefined) {
      return -1;
    }
    if (b === null || b === undefined) {
      return 1;
    }
    return a.localeCompare(b);
  };
  const columns: TableColumnsType<AccountReponse> = useMemo(() => {
    return [
      {
        title: 'アカウント名',
        dataIndex: 'accountId',
        width: '20%',
        sorter: (a, b) => doCompareStr(a.accountId, b.accountId),
      },
      {
        title: 'ユーザ名',
        dataIndex: 'userName',
        width: '20%',
        sorter: (a, b) => doCompareStr(a.userName, b.userName),
      },
      {
        title: 'メールアドレス',
        dataIndex: 'email',
        width: '20%',
        sorter: (a, b) => doCompareStr(a.email, b.email),
      },
      {
        title: '会社名',
        dataIndex: 'companyName',
        width: '14%',
        sorter: (a, b) => doCompareStr(a.companyName, b.companyName),
      },
      {
        title: '担当業務',
        dataIndex: 'businessType',
        width: '8%',
        sorter: (a, b) => {
          const aName = BusinessTypes.find((type) => type.key === a.businessType)?.value ?? '';
          const bName = BusinessTypes.find((type) => type.key === b.businessType)?.value ?? '';
          return doCompareStr(aName, bName);
        },
        render: (_, data) => (
          <div>
            {BusinessTypes.find((type) => type.key === data.businessType)?.value ?? ''}
          </div>
        ),
      },
      {
        title: '権限',
        dataIndex: 'role',
        width: '13%',
        sorter: (a, b) => {
          const aName = Roles.find((type) => type.key === a.role)?.value ?? '';
          const bName = Roles.find((type) => type.key === b.role)?.value ?? '';
          return doCompareStr(aName, bName);
        },
        render: (_, data) => (
          <div>
            {Roles.find((type) => type.key === data.role)?.value ?? ''}
          </div>
        ),
      },
      {
        title: '操作',
        dataIndex: 'delete',
        width: '5%',
        render: (_, data) => {
          return (
              <div>
                <Button
                  type="primary" danger
                  disabled={!data.canDelete}
                  onClick={() => {
                    setTargetUser(data);
                    showModal(true);
                  }}
                >
                  削除
                </Button>
              </div>
          );
        },
      },
    ];
  }, []);

  const errorText = useMemo(() => {
    if (errorCode === 0) {
      return undefined;
    }
    return <Alert message={Messages.ERR_OTHER} type="error"/>;
  }, [errorCode]);

  return (
    <div>
      {contextHolder}
      <h1>アカウント一覧画面</h1>
      <Modal
        open={modal}
        title="アカウント削除"
        onCancel={() => {
          showModal(false);
          setTargetUser(null);
        }}
        onOk={() => {
          handleDelete();
        }}
        okText="削除"
        okButtonProps={{ danger: true }}
      >
        <p>
          以下のアカウントを削除してもよろしいですか？<br/>
          削除が完了すると元に戻すことはできません。<br/><br/>
          アカウント名：{targetUser?.accountId}
        </p>
      </Modal>
      <div>
        <div className="section_no_backcolor">
          <div style={{ marginTop: '20px' }}>
            <Button type="primary" onClick={() => {
              navigator('/account_regist');
            }}>アカウント追加</Button>
          </div>
          <div style={{ marginTop: '20px' }}>
            <Table<AccountReponse>
              rowKey={(record) => `${record.accountId}_${record.userId}`}
              columns={columns}
              pagination={false}
              loading={false}
              locale={{ emptyText: errorText }}
              showHeader={data && data.length > 0}
              dataSource={data}/>
          </div>
        </div>
      </div>
    </div>
  );
};
